import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const Navigation: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavigation = (path: string) => {
    setIsMenuOpen(false);
    navigate(path);
  };

  const navItems = [
    { path: '/', label: 'Home' },
    { path: '/research', label: 'Research' },
    { path: '/team', label: 'Team' },
    { path: '/publications', label: 'Publications' },
    { path: '/collaborators', label: 'Collaborators' },
    { path: '/join-us', label: 'Join Us' },
    { path: '/contact', label: 'Contact' }
  ];

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <nav className={`bg-white sticky top-0 z-50 transition-shadow duration-300 ${isScrolled ? 'shadow-lg' : 'shadow-md'}`}>
      <div className="container mx-auto px-4 py-3 flex justify-between items-center">
        <button 
          onClick={() => handleNavigation('/')}
          className="flex items-center group"
        >
          <img 
            src="/logo.png" 
            alt="ModicLab Logo" 
            className="h-12 w-auto transition-transform group-hover:scale-105" 
          />
        </button>
        
        {/* Desktop Navigation */}
        <div className="hidden lg:flex space-x-6">
          {navItems.map((item) => (
            <button
              key={item.path}
              onClick={() => handleNavigation(item.path)}
              className={`
                relative px-3 py-2 text-sm font-medium transition-colors
                ${isActive(item.path) 
                  ? 'text-blue-600' 
                  : 'text-gray-600 hover:text-gray-900'
                }
              `}
            >
              {item.label}
              {isActive(item.path) && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"></span>
              )}
            </button>
          ))}
        </div>

        {/* Hamburger Menu Button */}
        <button 
          className="block lg:hidden p-2 focus:outline-none"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label="Toggle Menu"
        >
          <div className="w-6 h-6 relative">
            <span className={`absolute h-0.5 w-full bg-gray-600 transform transition-all duration-300 ${isMenuOpen ? 'rotate-45 top-2.5' : 'top-0'}`}></span>
            <span className={`absolute h-0.5 w-full bg-gray-600 top-2.5 transition-opacity duration-300 ${isMenuOpen ? 'opacity-0' : 'opacity-100'}`}></span>
            <span className={`absolute h-0.5 w-full bg-gray-600 transform transition-all duration-300 ${isMenuOpen ? '-rotate-45 top-2.5' : 'top-5'}`}></span>
          </div>
        </button>
      </div>

      {/* Mobile Navigation Dropdown */}
      <div 
        className={`
          absolute left-0 right-0 bg-white shadow-lg lg:hidden
          transition-all duration-300 ease-in-out
          ${isMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}
        `}
        style={{ 
          zIndex: 50,
          top: '100%',
          maxHeight: isMenuOpen ? '100vh' : '0',
          overflow: 'hidden'
        }}
      >
        <div className="container mx-auto px-4 py-2">
          <div className="flex flex-col">
            {navItems.map((item) => (
              <button
                key={item.path}
                onClick={() => handleNavigation(item.path)}
                className={`
                  text-left px-4 py-3 rounded-lg transition-colors
                  ${isActive(item.path)
                    ? 'bg-blue-50 text-blue-600 font-medium'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }
                `}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
